/**
 * Generato automaticamente.
 * OpenAPI
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  AmbitoDTO,
  AmbitoRequest,
  GetAmbitiParams,
  WsProblema,
  WsRispostaPaginataListAmbitoDTO,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Aggiorna una ambito.
 * @summary Modifica Ambito
 */
export const putAmbitiId = (
  id: string,
  ambitoRequest: AmbitoRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<AmbitoDTO>(
    {
      url: `/framework-cfg-domande/ambiti/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: ambitoRequest,
    },
    options,
  );
};

export const getPutAmbitiIdMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putAmbitiId>>,
    TError,
    { id: string; data: AmbitoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putAmbitiId>>,
  TError,
  { id: string; data: AmbitoRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putAmbitiId>>,
    { id: string; data: AmbitoRequest }
  > = props => {
    const { id, data } = props ?? {};

    return putAmbitiId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutAmbitiIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putAmbitiId>>
>;
export type PutAmbitiIdMutationBody = AmbitoRequest;
export type PutAmbitiIdMutationError = ErrorType<WsProblema>;

/**
 * @summary Modifica Ambito
 */
export const usePutAmbitiId = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putAmbitiId>>,
    TError,
    { id: string; data: AmbitoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutAmbitiIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Rimuove il collegamento ambito-tipoDomanda da tutti i TipiDomande.
 * @summary Elimina Ambito
 */
export const deleteAmbitiId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    { url: `/framework-cfg-domande/ambiti/${id}`, method: 'delete' },
    options,
  );
};

export const getDeleteAmbitiIdMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteAmbitiId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteAmbitiId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteAmbitiId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteAmbitiId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteAmbitiIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteAmbitiId>>
>;

export type DeleteAmbitiIdMutationError = ErrorType<WsProblema>;

/**
 * @summary Elimina Ambito
 */
export const useDeleteAmbitiId = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteAmbitiId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteAmbitiIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ottiene l'elenco degli ambiti
 * @summary Elenco Ambiti
 */
export const getAmbiti = (
  params?: GetAmbitiParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListAmbitoDTO>(
    { url: `/framework-cfg-domande/ambiti`, method: 'get', params, signal },
    options,
  );
};

export const getGetAmbitiQueryKey = (params?: GetAmbitiParams) =>
  [`/framework-cfg-domande/ambiti`, ...(params ? [params] : [])] as const;

export const getGetAmbitiQueryOptions = <
  TData = Awaited<ReturnType<typeof getAmbiti>>,
  TError = ErrorType<unknown>,
>(
  params?: GetAmbitiParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getAmbiti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<Awaited<ReturnType<typeof getAmbiti>>, TError, TData> & {
  queryKey: QueryKey;
} => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAmbitiQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAmbiti>>> = ({
    signal,
  }) => getAmbiti(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetAmbitiQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAmbiti>>
>;
export type GetAmbitiQueryError = ErrorType<unknown>;

/**
 * @summary Elenco Ambiti
 */
export const useGetAmbiti = <
  TData = Awaited<ReturnType<typeof getAmbiti>>,
  TError = ErrorType<unknown>,
>(
  params?: GetAmbitiParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getAmbiti>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetAmbitiQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Crea un nuovo ambito.
 * @summary Crea Ambito
 */
export const postAmbiti = (
  ambitoRequest: AmbitoRequest,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<AmbitoDTO>(
    {
      url: `/framework-cfg-domande/ambiti`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: ambitoRequest,
    },
    options,
  );
};

export const getPostAmbitiMutationOptions = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postAmbiti>>,
    TError,
    { data: AmbitoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postAmbiti>>,
  TError,
  { data: AmbitoRequest },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postAmbiti>>,
    { data: AmbitoRequest }
  > = props => {
    const { data } = props ?? {};

    return postAmbiti(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostAmbitiMutationResult = NonNullable<
  Awaited<ReturnType<typeof postAmbiti>>
>;
export type PostAmbitiMutationBody = AmbitoRequest;
export type PostAmbitiMutationError = ErrorType<WsProblema>;

/**
 * @summary Crea Ambito
 */
export const usePostAmbiti = <
  TError = ErrorType<WsProblema>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postAmbiti>>,
    TError,
    { data: AmbitoRequest },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostAmbitiMutationOptions(options);

  return useMutation(mutationOptions);
};
